import { FieldType } from '@app/origami-api/constants/field-type';
import { FieldConfig } from '@app/shared/forms/dynamic-form/fields.service';
import { TopicField } from '@app/origami-api/utils/topic-utils';
import { DateTime } from 'luxon';

export namespace Converter {
  export class DateAndTime {
    static readonly MAIN_DATE_FORMAT: string = 'DD MMM YYYY';
    static readonly MAIN_DATE_AND_TIME_FORMAT: string = 'DD MMM YYYY hh:mm:ss A';
    static readonly MAIN_TIME_FORMAT: string = 'hh:mm:ss A';
    static readonly DAY_DATE_AND_TIME_FORMAT: string = 'ddd DD/MM/YYYY, h:mm a';
    static readonly DATE_AND_TIME_FORMAT_WITH_SECONDS: string = 'DD/MM/YYYY, h:mm:ss a';
    static readonly DAY_DATE_AND_TIME_FORMAT_WITH_SECONDS: string = 'ddd DD/MM/YYYY, h:mm:ss a';
    static readonly DAY_AND_DATE_FORMAT: string = 'ddd DD/MM/YYYY';
    static readonly FIELDS_DATE_FORMAT: string = 'MM/DD/YYYY';
    static readonly FIELDS_DATE_FORMAT_WITH_SECONDS: string = 'MM/DD/YYYY HH:mm:ss';
    static readonly FIELDS_DATE_FORMAT_WITH_MILLISECONDS: string = 'MM/DD/YYYY h:mm:ss.SSS a';
    static readonly FIELDS_DATETIME_FORMAT: string = 'YYYY-MM-DD HH:mm:ss'; // 2015-06-08 13:19:22.027
    static readonly FIELDS_DATETIME_FORMAT_PACKAGES: string = 'D MMM YYYY HH:mm:ss';
    static readonly DAY: string = 'D';
    static readonly DAY_MONTH: string = 'D MMM';
    static readonly DAY_MONTH_YEAR: string = 'D MMM YYYY';

    static toCSharpString(date: Date, skipTimeZone?: boolean): string {
      if (date && date.getTime) {
        if (skipTimeZone) {
          return `/Date(${date.getTime()})/`;
        } else {
          return `/Date(${date.getTime()}-0000)/`;
        }
      }
    }

    static fromCSharpString(date: string): Date {
      const re = new RegExp('\\/Date\\(([-+])?(\\d+)(?:[+-]\\d{4})?\\)\\/');
      const r = (date || '').match(re);

      return r ? new Date(+((r[1] || '') + r[2])) : null;
    }

    static getTimeZoneOffset(msJSONDateWithTZ: string): number {
      if (msJSONDateWithTZ && msJSONDateWithTZ.length > 12) {
        const timeZoneSubStr = msJSONDateWithTZ.substring(msJSONDateWithTZ.length - 7, msJSONDateWithTZ.length - 2);
        if (['+', '-'].includes(timeZoneSubStr[0])) {
          const multiplier = timeZoneSubStr[0] === '-' ? -1 : 1;
          const HHStr = timeZoneSubStr.substring(1, 3);
          const mmStr = timeZoneSubStr.substring(3);
          return multiplier * (+HHStr * 60 * 60 * 1000 + +mmStr * 60 * 1000);
        }
      }
      return 0;
    }

    static toKendoDateFormat(date: string): string {
      return date && date.replace(/D|Y/g, (s) => s.toLowerCase());
    }

    static parseStringToJSDate(dateString: string | Date, timezoneName?: string, customFormat?: string): Date {
      if (!dateString) {
        return null;
      }
      const options = {
        ...(timezoneName && { zone: timezoneName }),
      };
      let dateTime: DateTime;
      if (dateString instanceof Date) {
        dateTime = DateTime.fromJSDate(dateString, options);
      } else {
        [
          ...(customFormat ? [customFormat] : []), // add customFormat to the top if it exists
          'MM/dd/yyyy',
          'yyyy-MM-dd HH:mm:ss.SSS',
          'yyyy-MM-dd HH:mm:ss',
          'M/dd/yyyy',
          'M/d/yyyy',
          'MM/dd/yy h:mm:ss a',
          'MM/dd/yy  h:mm:ss a',
          'dd.MM.yyyy',
          'dd-MM-yyyy h:mm:ss a',
          'dd.MM.yyyy h:mm:ss a',
        ].some((format) => {
          dateTime = DateTime.fromFormat(dateString, format, options);
          return dateTime.isValid;
        });
      }
      if (!dateTime.isValid) {
        dateTime = DateTime.fromISO(dateString, options);
      }
      return dateTime.isValid ? dateTime.toJSDate() : new Date(dateString);
    }
  }

  export class TopicFields {
    static getPreparedFieldValueUI(value: string | boolean, fieldType: number, timezoneName?: string): any {
      switch (fieldType) {
        case FieldType.Date:
          return value ? DateAndTime.parseStringToJSDate(value as string | Date, timezoneName) : null;
        case FieldType.Boolean:
          return value === '0' || value === false ? false : value ? true : null;
        case FieldType.Number:
          if (Array.isArray(value)) {
            return value;
          }

          return value == null || value === '' || isNaN(+value) ? null : +value; // unary plus converts undefined to NaN
        default:
          return value;
      }
    }

    static getPreparedFieldValueDB(value: any, fieldType: FieldType): string {
      switch (fieldType) {
        case FieldType.Date:
          return value ? Converter.DateAndTime.toCSharpString(value) : null;
        case FieldType.Boolean:
          return value === false ? '0' : value ? '1' : null;
        case FieldType.Number:
        default:
          return value === null || value === undefined ? null : value.toString();
      }
    }

    static fieldConfigToTopicField(fieldConfig: FieldConfig): TopicField {
      return <TopicField>{
        FieldName: fieldConfig.name,
        FieldType: fieldConfig.type,
        Fieldvalue: fieldConfig.value,
      };
    }
  }

  export class Bytes {
    static toString(value: number) {
      if (typeof value !== 'number') {
        return value;
      }
      if (value < 1024) {
        return value + ' bytes';
      }
      if (value >= 1024 && value < 1048576) {
        return (value / 1024).toFixed(1) + ' KB';
      }
      if (value >= 1048576 && value < 1073741824) {
        return (value / 1048576).toFixed(1) + ' MB';
      }
      return (value / 1073741824).toFixed(1) + ' GB';
    }
  }

  export class Strings {
    static htmlEncode(value) {
      return !value
        ? value
        : String(value).replace(/&/g, '&amp;').replace(/>/g, '&gt;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
    }

    static htmlDecode(value) {
      return !value
        ? value
        : String(value)
            .replace(/&gt;/g, '>')
            .replace(/&lt;/g, '<')
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&');
    }
  }
}

export function topicThreeStateRenderer(value: boolean) {
  if (value === null || value === undefined) {
    return tiStrings.app.misc.na;
  }
  return value ? tiStrings.app.misc.registered : tiStrings.app.misc.notRegistered;
}
